import { type ReactNode, useRef, useState } from "react";
import { CommandDialog as BaseCommandDialog } from "../../ui/command-dialog";
import { TextAreaField } from "../../ui/fields";
import { STAFF_ONLY_LABEL } from "../../ui/staff-only-note";
import type { AnyRequestCommand, RequestCommandOutcome, RequestCommandResult } from "../commands";
import type { RequestFailure } from "../errors";

// The shell of every request command dialog. It adds what is special to the request lifecycle on top of the generic `CommandDialog` of the
// kit (`src/ui/command-dialog`): the internal reason (every staff command needs one, 1 to 500 characters, staff only), and the mapping of
// a `RequestFailure` to what the kit shows. The kit owns the rules every confirm dialog shares: nothing is sent before confirm, Escape and
// Cancel are blocked while the command runs, a failure is inline, a retry keeps the key.
//
// A dialog that failed in a way that cannot work again (a stale version, a request that moved, no access) offers Close only: the page behind
// it has already read the request again. A failure that may work on a second try (no answer, a busy server) keeps the form and says "Try
// again". A 422 keeps the form too, so the staff member can fix the field.
//
// The idempotency key: the request commands keep their own (`commands.ts`: one key for each body, scoped to the dialog session), so the
// key the kit passes to `mutate` is not used here.

const REASON_MAX = 500;

type CommandBuild =
  /** The command to send, built from the fields of the dialog. */
  | Readonly<{ ok: true; command: (reason: string) => AnyRequestCommand }>
  /** A field is invalid: the dialog has set its own error and the first invalid control takes focus. */
  | Readonly<{ ok: false }>;

type Props = Readonly<{
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  /** Commands without a reason (an internal note) set this to `false`. */
  needsReason?: boolean;
  reasonLabel?: string;
  /** The extra fields of this command. */
  children?: ReactNode;
  /** An extra line of help under a failure of this command (for example what a refusal means for this action). */
  failureHint?: (failure: RequestFailure) => string | null;
  run: (command: AnyRequestCommand) => Promise<RequestCommandResult>;
  /** Builds the command from the dialog's own fields. Called on submit, after the reason is checked. */
  build: () => CommandBuild;
  onDone: (outcome: RequestCommandOutcome) => void;
  onClose: () => void;
}>;

export function RequestCommandDialog({
  title,
  description,
  confirmLabel,
  tone = "primary",
  needsReason = true,
  reasonLabel = "Reason",
  children,
  failureHint,
  run,
  build,
  onDone,
  onClose,
}: Props) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  // The command built by the last successful check; `mutate` sends it.
  const command = useRef<AnyRequestCommand | null>(null);

  function validate(): boolean {
    const trimmed = reason.trim();
    const missing = needsReason
      ? trimmed === ""
        ? "Write a reason."
        : trimmed.length > REASON_MAX
          ? `Use at most ${REASON_MAX} characters.`
          : undefined
      : undefined;
    setReasonError(missing);
    // `build()` runs even when the reason is missing, so every invalid field shows its error at once.
    const built = build();
    if (missing !== undefined || !built.ok) return false;
    command.current = built.command(trimmed);
    return true;
  }

  return (
    <BaseCommandDialog<RequestCommandOutcome>
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      tone={tone}
      // The request dialogs put the confirm button first (the kit default is Cancel first).
      order="confirm-first"
      validate={validate}
      mutate={async () => {
        const result = await run(command.current as AnyRequestCommand);
        if (result.ok) return { ok: true, value: result.outcome };
        return {
          ok: false,
          failure: {
            message: result.failure.message,
            hint: failureHint?.(result.failure),
            retryable: result.failure.retryable,
            keepForm: result.failure.kind === "invalid",
            tone: result.failure.kind === "conflict" ? "conflict" : "error",
          },
        };
      }}
      onDone={onDone}
      onClose={onClose}
    >
      {() => (
        <>
          {children}
          {needsReason ? (
            <TextAreaField
              id="request-reason"
              label={reasonLabel}
              hint={`${STAFF_ONLY_LABEL} It goes into the history. The customer never sees it.`}
              value={reason}
              rows={3}
              maxLength={REASON_MAX}
              required
              error={reasonError}
              onChange={setReason}
            />
          ) : null}
        </>
      )}
    </BaseCommandDialog>
  );
}
